/**
 * Authentication Hooks
 * React hooks for managing authentication and login
 */

import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from './useBaseHooks';
import { adminApiClient } from './adminApiClient';
import {
  LoginRequest,
  LoginResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  AdminUserResponse,
  ApiResponse,
  VerifyOTPRequest,
  PasswordByOTPRequest
} from './adminTypes';

/**
 * Hook to handle admin login
 * @param options - Hook options
 */
export function useLogin(
  options: {
    onSuccess?: (data: LoginResponse, credentials: LoginRequest) => void;
    onError?: (error: Error, credentials: LoginRequest) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<LoginResponse, LoginRequest>(
    async (credentials: LoginRequest) => {
      // API expects userName field, not username
      const requestBody = {
        userName: credentials.username || credentials.userName,
        password: credentials.password,
      };

      const response = await adminApiClient.post<LoginResponse>(
        '/api/admin/login',
        requestBody
      );

      // Store token if present in response
      if (response.token) {
        adminApiClient.setAuthToken(response.token);
        localStorage.setItem('authToken', response.token);
      }

      // Store user data if present
      if (response.userName || response.email) {
        localStorage.setItem('user', JSON.stringify({
          id: response.id,
          userName: response.userName,
          email: response.email,
          roles: response.roles || [],
          firstName: response.firstName,
          lastName: response.lastName,
        }));
      }

      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to handle logout
 */
export function useLogout(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { onSuccess, onError } = options;
  const [loading, setLoading] = useState(false);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      // Call logout API endpoint
      try {
        await adminApiClient.post<ApiResponse>('/api/admin/logout');
      } catch (apiError) {
        // Continue with local cleanup even if API call fails
      }

      // Clear token from client
      adminApiClient.clearAuthToken();

      // Clear token from localStorage
      localStorage.removeItem('authToken');

      // Clear any other auth-related data
      localStorage.removeItem('user');

      onSuccess?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Logout failed');
      onError?.(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, onError]);

  return {
    logout,
    loading,
  };
}

/**
 * Hook to check authentication status
 */
export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      adminApiClient.setAuthToken(token);
      return true;
    }
    return false;
  });

  const login = useCallback((token: string) => {
    adminApiClient.setAuthToken(token);
    localStorage.setItem('authToken', token);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    adminApiClient.clearAuthToken();
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
  }, []);

  return {
    isAuthenticated,
    login,
    logout,
    token: adminApiClient.getAuthToken(),
  };
}

/**
 * Hook to restore authentication from localStorage
 */
export function useRestoreAuth() {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const restore = useCallback(() => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        adminApiClient.setAuthToken(token);
        setIsAuthenticated(true);
      }
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-restore on mount
  useState(() => {
    restore();
  });

  return {
    loading,
    isAuthenticated,
    restore,
  };
}

/**
 * Hook to handle forgot password
 */
export function useForgotPassword(
  options: {
    onSuccess?: (data: ApiResponse, request: ForgotPasswordRequest) => void;
    onError?: (error: Error, request: ForgotPasswordRequest) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse, ForgotPasswordRequest>(
    async (request: ForgotPasswordRequest) => {
      const response = await adminApiClient.put<ApiResponse>('/api/admin/send-otp',
        null, // ❗ no body
        {
          params: {
            email: request.email,
            type: "ADMIN"
          }
        }
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to handle verify otp
 */
export function uesVerifyOTP(
  options: {
    onSuccess?: (data: ApiResponse, request: VerifyOTPRequest) => void;
    onError?: (error: Error, request: VerifyOTPRequest) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse, VerifyOTPRequest>(
    async (request: VerifyOTPRequest) => {
      const response = await adminApiClient.put<ApiResponse>('/api/admin/verify-otp',
        null, // ❗ no body
        {
          params: {
            email: request.email,
            type: "ADMIN",
            otp: request.resetCode
          }
        }
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to handle change password
 */
export function useChangePasswordByOtp(
  options: {
    onSuccess?: (data: ApiResponse, request: PasswordByOTPRequest) => void;
    onError?: (error: Error, request: PasswordByOTPRequest) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse, PasswordByOTPRequest>(
    async (request: PasswordByOTPRequest) => {
      const response = await adminApiClient.put<ApiResponse>('/api/admin/change-password',
        null, // ❗ no body
        {
          params: {
            email: request.email,
            type: "ADMIN",
            newPassword: request.newPassword
          }
        }
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to handle reset password
 */
export function useResetPassword(
  options: {
    onSuccess?: (data: ApiResponse, request: ResetPasswordRequest) => void;
    onError?: (error: Error, request: ResetPasswordRequest) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse, ResetPasswordRequest>(
    async (request: ResetPasswordRequest) => {
      const response = await adminApiClient.post<ApiResponse>(
        '/api/admin/reset-password',
        request
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to handle change password (reset)
 * API endpoint: GET /api/admin/user_reset_password/{id}
 */
export function useChangePassword(
  options: {
    onSuccess?: (data: ApiResponse, userId: number | string) => void;
    onError?: (error: Error, userId: number | string) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse, void>(
    async () => {
      // Get user ID from localStorage - check both 'user' and 'auth_user' keys for compatibility
      const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user') || localStorage.getItem('user');
      if (!userStr) {
        throw new Error('User not found. Please log in again.');
      }
      const user = JSON.parse(userStr);
      const userId = user.id;

      if (!userId) {
        throw new Error('User ID not found. Please log in again.');
      }

      // API endpoint: GET /api/admin/user_reset_password/{id}
      const response = await adminApiClient.get<ApiResponse>(
        `/api/admin/user_reset_password/${userId}`
      );
      return response;
    },
    {
      onSuccess: (data) => {
        const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user') || localStorage.getItem('user');
        const userId = userStr ? JSON.parse(userStr).id : undefined;
        onSuccess?.(data, userId);
      },
      onError: (error) => {
        const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user') || localStorage.getItem('user');
        const userId = userStr ? JSON.parse(userStr).id : undefined;
        onError?.(error, userId);
      },
    }
  );

  return mutation;
}

/**
 * Hook for admin to force-reset another user's password by ID
 * Endpoint: GET /api/admin/user_reset_password/{id}
 */
export function useAdminForceResetPassword(
  options: {
    onSuccess?: (data: ApiResponse, userId: number | string) => void;
    onError?: (error: Error, userId: number | string) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse, number | string>(
    async (userId: number | string) => {
      const response = await adminApiClient.get<ApiResponse>(
        `/api/admin/user_reset_password/${userId}`
      );
      return response;
    },
    {
      onSuccess: (data, userId) => {
        onSuccess?.(data, userId);
      },
      onError: (error, userId) => {
        onError?.(error, userId);
      },
    }
  );

  return mutation;
}

/**
 * Hook for admin to reset another user's password
 * Endpoint: GET /api/admin/user_change_password/{userId}?existingPassword=xxx&newPassword=yyy
 */
export function useAdminResetUserPassword(
  options: {
    onSuccess?: (data: ApiResponse, variables: { userId: number | string; existingPassword: string; newPassword: string; confirmPassword: string }) => void;
    onError?: (error: Error, variables: { userId: number | string; existingPassword: string; newPassword: string; confirmPassword: string }) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse, { userId: number | string; existingPassword: string; newPassword: string; confirmPassword: string }>(
    async ({ userId, existingPassword, newPassword }) => {
      const userIdNum = typeof userId === "string" ? parseInt(userId, 10) : userId;

      // API endpoint: GET /api/admin/user_change_password/{userId}?existingPassword=xxx&newPassword=yyy
      // Using GET request with query parameters as per API specification
      return await adminApiClient.get<ApiResponse>(
        `/api/admin/user_change_password/${userIdNum}`,
        {
          existingPassword: existingPassword,
          newPassword: newPassword,
        }
      );
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to verify current password before changing it
 * Endpoint: GET /api/admin/check_existing_password/{id}?password=
 * Returns true if password matches, false otherwise
 */
export function useCheckExistingPassword() {
  const checkPassword = async (userId: number | string, password: string): Promise<boolean> => {
    const userIdNum = typeof userId === 'string' ? parseInt(userId, 10) : userId;
    const response = await adminApiClient.get<any>(
      `/api/admin/check_existing_password/${userIdNum}`,
      { password }
    );
    // API returns true/false directly or wrapped in data
    if (typeof response === 'boolean') return response;
    if (typeof response?.data === 'boolean') return response.data;
    return false;
  };
  return { checkPassword };
}

/**
 * Hook to get current user profile
 */
export function useGetCurrentUser(
  options: {
    enabled?: boolean;
    onSuccess?: (data: AdminUserResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  const query = useQuery<AdminUserResponse>(
    async () => {
      const response = await adminApiClient.get<AdminUserResponse>('/api/admin/me');
      return response;
    },
    {
      enabled,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );

  return query;
}
